import { UGC_CREDITS } from "../lib/ugcPricing";
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X, Zap, Crown, Building2, Sparkles, ArrowRight, Loader2, ArrowLeft, Coins, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { BASE_URL, creditsAPI } from '../lib/api';

const API_URL = BASE_URL;

const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isYearly, setIsYearly] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [creditBundles, setCreditBundles] = useState([]);
  const [pppDiscount, setPppDiscount] = useState(0);
  const [creditPurchaseLoading, setCreditPurchaseLoading] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchCurrentSubscription();
    fetchCreditBundles();
    
    // Check for hash anchor to scroll to credits section
    if (window.location.hash === '#credits') {
      setTimeout(() => {
        document.getElementById('credits')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscription/plans`);
      const data = await response.json();
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pricing plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentPlan(data.plan);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchCreditBundles = async () => {
    try {
      const response = await creditsAPI.getBundles();
      const data = response.data || response;  // Handle both raw data and axios response
      setCreditBundles(data.bundles || []);
      
      // Check for PPP discount
      const firstBundle = data.bundles?.[0];
      if (firstBundle?.original_price && firstBundle.original_price > firstBundle.price) {
        const discount = Math.round((1 - firstBundle.price / firstBundle.original_price) * 100);
        setPppDiscount(discount);
      }
    } catch (error) {
      console.error('Error fetching credit bundles:', error);
    }
  };

  const handleCreditPurchase = async (bundleId) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please log in to purchase credits',
        variant: 'destructive'
      });
      navigate('/login?redirect=/pricing#credits');
      return;
    }

    setCreditPurchaseLoading(bundleId);

    try {
      const response = await creditsAPI.purchase(bundleId);
      const result = response.data || response;
      
      if (result.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create checkout session',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Credit purchase error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Purchase failed',
        variant: 'destructive'
      });
    } finally {
      setCreditPurchaseLoading(null);
    }
  };

  const handleCheckout = async (planId) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please log in to subscribe',
        variant: 'destructive'
      });
      navigate('/login?redirect=/pricing');
      return;
    }

    setCheckoutLoading(planId);

    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: planId,
          billing_cycle: isYearly ? 'yearly' : 'monthly',
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Checkout failed');
      }

      const data = await response.json();
      
      // Redirect to Mollie checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Checkout Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setCheckoutLoading(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'standard': return <Zap className="h-6 w-6" />;
      case 'premium': return <Sparkles className="h-6 w-6" />;
      case 'pro': return <Crown className="h-6 w-6" />;
      case 'enterprise': return <Building2 className="h-6 w-6" />;
      default: return <Zap className="h-6 w-6" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'standard': return 'from-blue-500 to-blue-600';
      case 'premium': return 'from-purple-500 to-purple-600';
      case 'pro': return 'from-amber-500 to-orange-500';
      case 'enterprise': return 'from-slate-700 to-slate-800';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  const paidPlans = plans.filter(p => p.id !== 'free');

  // Handle back navigation
  const handleGoBack = () => {
    // Check if there's browser history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to dashboard if no history
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={handleGoBack}
          className="mb-6 text-slate-400 hover:text-white hover:bg-slate-800"
          data-testid="pricing-back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-slate-400 mb-8">
            Create viral videos with AI-powered tools
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <Label htmlFor="billing-toggle" className={`text-lg ${!isYearly ? 'text-white' : 'text-slate-400'}`}>
              Monthly
            </Label>
            <Switch
              id="billing-toggle"
              checked={isYearly}
              onCheckedChange={setIsYearly}
              data-testid="billing-toggle"
            />
            <Label htmlFor="billing-toggle" className={`text-lg ${isYearly ? 'text-white' : 'text-slate-400'}`}>
              Yearly
            </Label>
            {isYearly && (
              <Badge variant="secondary" className="bg-green-500/20 text-green-400 border-green-500/30">
                Save 2 months!
              </Badge>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {paidPlans.map((plan) => {
            const isCurrentPlan = currentPlan === plan.id;
            const isPopular = plan.id === 'premium';
            const price = isYearly ? plan.yearly_price : plan.monthly_price;
            const monthlyEquivalent = isYearly ? (plan.yearly_price / 12).toFixed(2) : plan.monthly_price;

            return (
              <Card 
                key={plan.id}
                className={`relative bg-slate-800/50 border-slate-700 overflow-hidden ${isPopular ? 'ring-2 ring-purple-500' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                
                <CardHeader>
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${getPlanColor(plan.id)} flex items-center justify-center text-white mb-4`}>
                    {getPlanIcon(plan.id)}
                  </div>
                  <CardTitle className="text-white text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-slate-400">
                    {plan.video_limit} credits/month • {plan.max_duration}s max
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">£{monthlyEquivalent}</span>
                    <span className="text-slate-400">/month</span>
                    {isYearly && (
                      <div className="text-sm text-green-400 mt-1">
                        £{price}/year (save £{plan.yearly_savings})
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.id !== 'free' && (
                      <li className="flex items-start gap-2 text-sm text-slate-300" data-testid={`plan-ugc-pricing-${plan.id}`}>
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>
                          UGC ads: Standard {UGC_CREDITS.standard} credits ({UGC_CREDITS.standard_30s} for 30s)
                          {plan.ugc_access ? ` · Premium ${UGC_CREDITS.premium} (${UGC_CREDITS.premium_30s} for 30s)` : ""}
                          <span className="block text-slate-400 text-xs mt-0.5">
                            Up to {Math.floor(plan.video_limit / UGC_CREDITS.standard)} Standard
                            {plan.ugc_access ? ` or ${Math.floor(plan.video_limit / UGC_CREDITS.premium)} Premium` : ""} ads a month
                            {!plan.ugc_access && " · Premium with the UGC add-on or Pro"}
                          </span>
                        </span>
                      </li>
                    )}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button
                    className={`w-full ${isCurrentPlan ? 'bg-slate-600' : `bg-gradient-to-r ${getPlanColor(plan.id)} hover:opacity-90`}`}
                    disabled={isCurrentPlan || checkoutLoading === plan.id}
                    onClick={() => handleCheckout(plan.id)}
                    data-testid={`checkout-btn-${plan.id}`}
                  >
                    {checkoutLoading === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    {isCurrentPlan ? 'Current Plan' : 'Get Started'}
                    {!isCurrentPlan && <ArrowRight className="h-4 w-4 ml-2" />}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Duration Add-ons Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Need Longer Videos?
          </h2>
          <div className="max-w-3xl mx-auto">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Duration Add-ons</CardTitle>
                <CardDescription className="text-slate-400">
                  Upgrade video duration on a per-video basis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-white mb-1">£2.00</div>
                    <div className="text-slate-400 text-sm">10s → 15s</div>
                  </div>
                  <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-white mb-1">£3.50</div>
                    <div className="text-slate-400 text-sm">10s → 30s</div>
                  </div>
                  <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-white mb-1">£2.00</div>
                    <div className="text-slate-400 text-sm">15s → 30s</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Enterprise CTA */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-slate-800 to-slate-700 border-slate-600 max-w-2xl mx-auto">
            <CardContent className="py-8">
              <Building2 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Need More?</h3>
              <p className="text-slate-400 mb-6">
                Enterprise plans with custom video limits, API access, and dedicated support
              </p>
              <Button 
                variant="outline" 
                className="border-slate-500 text-white hover:bg-slate-600"
                onClick={() => window.location.href = 'mailto:enterprise@igoviralcreator.io'}
              >
                Contact Sales
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Pay-Per-Use Credits Section */}
        <div id="credits" className="mt-20 pt-8 border-t border-slate-700">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
              <Coins className="w-3 h-3 mr-1" />
              Pay As You Go
            </Badge>
            <h2 className="text-3xl font-bold text-white mb-4">
              Credit Bundles
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              No subscription required. Buy credits and use them whenever you want. 
              Credits never expire and everyone gets 1 free credit daily!
            </p>
            
            {/* PPP Discount Banner */}
            {pppDiscount > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
                <Globe className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-400 font-medium">
                  {pppDiscount}% regional discount applied
                </span>
              </div>
            )}
          </div>

          {/* Credit Bundles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" data-testid="pricing-credit-bundles-grid">
            {creditBundles.map((bundle) => (
              <Card 
                key={bundle.id}
                className={`relative ${
                  bundle.popular 
                    ? 'border-yellow-500/50 bg-yellow-500/5' 
                    : 'border-slate-700 bg-slate-800/50'
                }`}
                data-testid={`pricing-bundle-card-${bundle.id}`}
              >
                {bundle.popular && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-yellow-500 text-black">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-white" data-testid={`pricing-bundle-name-${bundle.id}`}>{bundle.name}</CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-white" data-testid={`pricing-bundle-price-${bundle.id}`}>${bundle.price.toFixed(2)}</span>
                    {bundle.original_price && bundle.original_price > bundle.price && (
                      <span className="ml-2 text-sm text-slate-500 line-through" data-testid={`pricing-bundle-original-price-${bundle.id}`}>
                        ${bundle.original_price.toFixed(2)}
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Coins className="w-5 h-5 text-yellow-500" />
                    <span className="text-xl font-semibold text-white" data-testid={`pricing-bundle-credits-${bundle.id}`}>{bundle.credits} credits</span>
                  </div>
                  <p className="text-sm text-slate-400" data-testid={`pricing-bundle-price-per-credit-${bundle.id}`}>
                    ${bundle.price_per_credit}/credit
                  </p>
                  {bundle.savings_percent > 0 && (
                    <Badge variant="outline" className="mt-2 text-green-400 border-green-400/30" data-testid={`pricing-bundle-savings-${bundle.id}`}>
                      Save {bundle.savings_percent}%
                    </Badge>
                  )}
                </CardContent>
                <CardFooter>
                  <Button
                    className={`w-full ${
                      bundle.popular
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black'
                        : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                    onClick={() => handleCreditPurchase(bundle.id)}
                    disabled={creditPurchaseLoading === bundle.id}
                    data-testid={`pricing-buy-bundle-btn-${bundle.id}`}
                  >
                    {creditPurchaseLoading === bundle.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>Buy Now</>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Credit Usage Info */}
          <div className="mt-12 max-w-3xl mx-auto">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  What can you create with credits?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="flex justify-between p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-slate-400">Fast Video</span>
                    <span className="text-white font-medium">1 credit</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-slate-400">Premium Video</span>
                    <span className="text-white font-medium">3 credits</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-slate-400">UGC Ad</span>
                    <span className="text-white font-medium">2-5 credits</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-slate-400">Viral Maker</span>
                    <span className="text-white font-medium">2 credits</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-slate-400">Voice Clone</span>
                    <span className="text-white font-medium">1 credit</span>
                  </div>
                  <div className="flex justify-between p-3 bg-slate-700/30 rounded-lg">
                    <span className="text-slate-400">AI Image</span>
                    <span className="text-white font-medium">1 credit</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 text-center text-slate-400 text-sm">
          <p>All plans include cloud storage and customer support.</p>
          <p className="mt-2">Cancel anytime. No hidden fees. Credits never expire.</p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
